import { useAppTheme } from "@hooks/useAppTheme";
import { Box } from "@memorang/ui";
import { ProethicaTestBadge } from "components/ProethicaTestBadge";
import { Image } from "expo-image";
import { Avatar } from "react-native-paper";

export const getModuleFallbackIcon = ({
	locked,
	isTest,
	icon,
}: {
	locked?: boolean;
	isTest?: boolean;
	icon?: string;
}): string => {
	if (locked) {
		return "lock-outline";
	}
	if (isTest) {
		return icon ?? "clipboard-text-outline";
	}
	return "book-open-outline";
};

type Props = {
	size: number;
	title: string;
	thumbnailSource?: string | number | { uri: string };
	thumbnailUrl?: string;
	locked?: boolean;
	isTest?: boolean;
	testKind?: "pretest" | "posttest";
	icon?: string;
};

export const ModuleThumbnail = ({
	size,
	title,
	thumbnailSource,
	thumbnailUrl,
	locked,
	isTest,
	testKind,
	icon,
}: Props) => {
	const theme = useAppTheme();
	const finalThumbnailSource =
		thumbnailSource ?? (thumbnailUrl ? { uri: thumbnailUrl } : undefined);
	const fallbackIcon = getModuleFallbackIcon({ locked, isTest, icon });

	return (
		<Box style={{ position: "relative", width: size, height: size }}>
			{finalThumbnailSource ? (
				<Image
					source={finalThumbnailSource}
					style={{
						width: size,
						height: size,
						borderRadius: theme.roundness,
						opacity: locked ? 0.5 : 1,
					}}
					contentFit="contain"
					alt={title}
				/>
			) : isTest && testKind ? (
				<ProethicaTestBadge kind={testKind} size={size} locked={locked} />
			) : (
				<Box
					style={{
						width: size,
						height: size,
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surfaceVariant,
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Avatar.Icon
						size={size / 2}
						icon={fallbackIcon}
						style={{ backgroundColor: "transparent" }}
						color={
							isTest ? theme.colors.primary : theme.colors.onSurfaceVariant
						}
					/>
				</Box>
			)}
			{locked && finalThumbnailSource ? (
				<Box
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: "rgba(0, 0, 0, 0.25)",
						justifyContent: "center",
						alignItems: "center",
						borderRadius: theme.roundness,
					}}
				>
					<Avatar.Icon
						size={28}
						icon="lock"
						style={{ backgroundColor: "transparent" }}
						color={theme.colors.white}
					/>
				</Box>
			) : null}
		</Box>
	);
};
